import React, { useState, useMemo, useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { Paper, Box, Typography, IconButton } from "@mui/material";
import circleInfoIcon from "../../../assets/images/SVG/circleInfo.svg";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import SystemContext from "../../../context/SystemContext";
import { isAuthenticated } from "../../../helpers/sessionHelpers";

const SurveyBalloon = () => {
  const { t } = useTranslation();
  const { updateTagUser } = useContext(SystemContext);
  const { activeUser, session } = useSelector((state) => state.session);
  const userEmail = activeUser?.email || session?.my_user?.email || session?.email;
  const hasVinculo = !!session?.my_user?.parent_sub;

  const [visible, setVisible] = useState(false);
  const [userClosed, setUserClosed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  const alreadyClicked = useMemo(() => {
    if (!userEmail) return true;
    const data = JSON.parse(localStorage.getItem("survey_balloon") || "{}");
    return !!data[userEmail];
  }, [userEmail]);

  const open = !userClosed && !alreadyClicked;

  const isActive = useMemo(() => {
    const now = new Date();
    const end = new Date("2026-06-30T23:59:59");
    return now <= end;
  }, []);

  const isUserAuthenticated = isAuthenticated();

  const shouldRender = useMemo(
    () => visible && open && isActive && isUserAuthenticated && !hasVinculo,
    [visible, open, isActive, isUserAuthenticated, hasVinculo]
  );

  const handleClick = () => {
    const tag = ["ABRIU_PESQUISA_NPS"];
    updateTagUser(tag);
    const data = JSON.parse(localStorage.getItem("survey_balloon") || "{}");
    data[userEmail] = true;
    localStorage.setItem("survey_balloon", JSON.stringify(data));
    setUserClosed(true);
  };

  return shouldRender ? (
    <Paper
      component={motion.a}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "tween", duration: 0.4, ease: "easeOut" }}
      href="https://forms.gle/5poZtxxd9vGBxrpb8"
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      sx={{
        position: "fixed",
        bottom: { xs: 66, lg: 24 },
        right: { xs: 68, lg: 24 },
        width: 350,
        borderRadius: "16px",
        zIndex: 1300,
        boxShadow: 3,
        cursor: "pointer",
        textDecoration: "none",
        display: "block",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          gap: 0
        }}
      >

        <Box
          sx={{
            bgcolor: "#EEE8FF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1.5,
            borderRadius: "8px 0 0 8px"
          }}
        >
          <img
            src={circleInfoIcon}
            alt={t("survey_balloon.icon_alt")}
            style={{
              width: 23,
              height: 23
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            px: 1.5,
            py: 1
          }}
        >
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "140%",
              letterSpacing: "0px"
            }}
          >
            {t("survey_balloon.title")}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "140%",
              letterSpacing: "0px",
              mt: 0.5,
              mb: 1
            }}
          >
            {t("survey_balloon.description")}
          </Typography>
          <Typography
            component="a"
            href="https://forms.gle/5poZtxxd9vGBxrpb8"
            target="_blank"
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 700,
              fontSize: "14px",
              lineHeight: "140%",
              letterSpacing: "0px",
              color: "#662D91",
              textDecoration: "underline",
            }}
          >
            {t("survey_balloon.cta")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            pt: 0.5
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setUserClosed(true); }}
          >
            <CloseIcon
              fontSize="small"
            />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  ) : null;
};

export default SurveyBalloon;